import React, { useState, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Edge,
  Node,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  Background,
  Connection,
  MiniMap,
  Controls,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { getProcessDetail, updateProcess, getProcessNodeMetaData } from '@/api/application';
import { useShallow } from 'zustand/react/shallow';
import { CustomNode, NODE_TYPE_ICON } from './components/CustomNode';
import useStore from './store';
import { workflowApi } from './workflowApi';
import { NODE_TYPE, NODE_TYPE_MAP } from './constants';

import { Button, Drawer, Empty, Form, Input, message, Space } from 'antd';
import NodeForm from './components/NodeForm';
// import nodeMetaData from './mock copy.json';
import { cloneDeep, isEmpty } from 'lodash-es';
import { useParams } from 'react-router-dom';
import { VariableOption } from './components/NodeForm/componentType';
import { useRequest } from 'ahooks';
import atoms from '@/styles/atoms.module.scss';

export type AppNode = Node;
export type AppState = {
  nodes: AppNode[];
  edges: Edge[];
  onNodesChange: OnNodesChange<AppNode>;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
  setNodes: (nodes: AppNode[]) => void;
  setEdges: (edges: Edge[]) => void;
};

const selector = (state: AppState) => ({
  nodes: state.nodes,
  edges: state.edges,
  onNodesChange: state.onNodesChange,
  onEdgesChange: state.onEdgesChange,
  onConnect: state.onConnect,
  setNodes: state.setNodes,
  setEdges: state.setEdges,
});

// =================== 类型定义 ===================
const nodeTypes = {
  [NODE_TYPE.START]: CustomNode,
  [NODE_TYPE.CONDITION]: CustomNode,
  [NODE_TYPE.KNOWLEDGE]: CustomNode,
  [NODE_TYPE.LLM]: CustomNode,
  [NODE_TYPE.PG]: CustomNode,
  [NODE_TYPE.END]: CustomNode,
};

const parseExpression = (expr: string) => {
  // 匹配格式: #变量名 操作符 '值'
  // 支持 ==、!=、>、<
  const regex = /^#?([\w]+)\s*(==|!=|>|<)\s*(['"])(.*?)\3$/;
  const match = expr.match(regex);

  if (!match) return {};

  const [, left, operator, , right] = match;
  return { left, operator, right };
};

// =================== 主组件 ===================
const Workflow: React.FC = () => {
  const { nodes, edges, onNodesChange, onEdgesChange, onConnect, setNodes, setEdges } = useStore(useShallow(selector));
  const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1 });

  const [panelPos, setPanelPos] = useState<{ x: number; y: number } | null>(null);
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // 新增状态

  // 新增状态：控制抽屉显示和编辑的节点数据
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [editingNode, setEditingNode] = useState<Node | null>(null);
  const [nodeConfig, setNodeConfig] = useState<any>();
  const [rfInstance, setRfInstance] = useState(null);
  const [editForm] = Form.useForm();
  // 从路由获取id
  const id = useParams().id;

  const { data: nodeMetaData } = useRequest(getProcessNodeMetaData);

  // 初始化数据
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getProcessDetail(id)
      .then(res => {
        if (!res?.definition) {
          setNodes([
            {
              id: 'start',
              type: NODE_TYPE.START as unknown as string,
              data: { label: '开始' },
              position: { x: 100, y: 250 },
            },
          ]);
          // workflowApi.getWorkflowData().then(res => {
          //   setNodes(
          //     res.nodes.map(i => ({
          //       ...i,
          //       type: i.nodeTypeId,
          //       data: i.config,
          //       id: i.nodeId,
          //     }))
          //   );
          //   setEdges(
          //     res.transitions.map(i => ({
          //       ...i,
          //       id: Math.random().toString(),
          //       source: i.fromNodeId,
          //       target: i.toNodeId,
          //     }))
          //   );
          // });
        } else {
          try {
            // 解析definition中的数据
            const workflowData = JSON.parse(res.definition);

            // 处理节点数据
            const allNodes: AppNode[] = workflowData.nodes.map((node: any) => ({
              id: node.id,
              type: node.type,
              position: node.position,
              data: node.data,
            }));

            // 处理边数据
            const allEdges: Edge[] = workflowData.edges.map((edge: any, index: number) => ({
              id: edge.id || `edge-${index}`, // 确保每条边都有唯一ID
              source: edge.source,
              target: edge.target,
            }));

            setNodes(allNodes);
            setEdges(allEdges);

            if (workflowData.viewport) {
              setViewport({
                x: workflowData.viewport.x || 0,
                y: workflowData.viewport.y || 0,
                zoom: workflowData.viewport.zoom || 1,
              });
            }
          } catch (error) {
            console.error('解析工作流数据失败:', error);
            message.error('工作流数据解析失败');
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setNodes, setEdges]);

  const getUpstreamNodeVariables = useCallback(
    (nodeId: string, nodes: AppNode[], edges: Edge[]) => {
      // 获取所有上游节点
      const upstreamNodeIds = new Set<string>();
      const traverseUpstream = (targetNodeId: string) => {
        const incomingEdges = edges.filter(edge => edge.target === targetNodeId);
        incomingEdges.forEach(edge => {
          upstreamNodeIds.add(edge.source);
          traverseUpstream(edge.source);
        });
      };

      traverseUpstream(nodeId);

      // 获取上游节点详情
      const upstreamNodes = nodes.filter(node => upstreamNodeIds.has(node.id));

      // 从nodeMetaData中获取这些节点类型的变量信息
      const upstreamVariables: VariableOption[] = [];

      upstreamNodes.forEach(node => {
        // 根据节点类型查找元数据
        const nodeMeta = nodeMetaData?.find((meta: any) => meta.id === node.type);

        if (nodeMeta && nodeMeta.variables && Array.isArray(nodeMeta.variables)) {
          // 将节点变量信息添加到结果中（不进行拼接）
          nodeMeta.variables.forEach((variable: any) => {
            upstreamVariables.push({
              value: variable.variableName, // 不拼接，只保留变量名
              nodeId: node.id,
              variableName: variable.variableName,
              nodeName: node.data?.label || node.id,
            });
          });
        }
      });

      return upstreamVariables;
    },
    [nodes, nodeMetaData, edges]
  );

  // 在组件内部添加计算可用目标节点的函数
  const getAvailableTargetNodes = useCallback(
    (currentNodeId: string, nodes: AppNode[], edges: Edge[]): { label: string; value: string }[] => {
      // 获取当前节点的所有上游节点ID（包括自身）
      const upstreamNodeIds = new Set<string>();

      const traverseUpstream = (targetNodeId: string) => {
        // 避免无限循环
        if (upstreamNodeIds.has(targetNodeId)) return;

        upstreamNodeIds.add(targetNodeId);
        const incomingEdges = edges.filter(edge => edge.target === targetNodeId);
        incomingEdges.forEach(edge => {
          traverseUpstream(edge.source);
        });
      };

      // 从当前节点开始遍历上游节点
      traverseUpstream(currentNodeId);

      // 返回不是上游节点的所有节点作为可选项
      return nodes
        .filter(node => !upstreamNodeIds.has(node.id)) // 排除当前节点及上游节点
        .map(node => ({
          label: node.data?.label || node.id,
          value: node.id,
        }));
    },
    []
  );

  const handleDeleteNode = useCallback(
    (id: string) => {
      setNodes(nodes.filter(n => n.id !== id));
      setEdges(edges.filter(e => e.source !== id && e.target !== id));
    },
    [nodes, edges]
  );

  const handleDeleteEdge = useCallback(
    (id: string) => {
      setEdges(edges.filter(e => e.id !== id));
    },
    [edges]
  );

  const onViewportChange = useCallback((viewport: { x: number; y: number; zoom: number }) => {
    setViewport(viewport);
  }, []);

  // 连接桩点击
  const onHandleClick = (e: React.MouseEvent, nodeId: string, handleType: string) => {
    e.stopPropagation();
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setPanelPos({ x: rect.right, y: rect.top });
    setActiveNode(nodeId);
  };

  // 点击空白关闭面板
  const handleCanvasClick = useCallback(() => {
    setPanelPos(null);
    setActiveNode(null);
  }, []);

  // 添加新节点并连接
  const handleAddNode = useCallback(() => {
    if (!activeNode) return;

    const newNodeId = `${nodes.length + 1}`;
    const activeNodeObj = nodes.find(n => n.id === activeNode);
    if (!activeNodeObj) return;

    const newNode: AppNode = {
      id: newNodeId,
      type: 'custom',
      position: {
        x: activeNodeObj.position.x + 200,
        y: activeNodeObj.position.y,
      },
      data: { label: `新节点 ${newNodeId}` },
    };

    const newEdge: Edge = {
      id: `e${activeNode}-${newNodeId}`,
      source: activeNode,
      target: newNodeId,
    };
    setNodes([...nodes, newNode]);
    setEdges([...edges, newEdge]);
    setPanelPos(null);
    setActiveNode(null);
  }, [activeNode, nodes]);

  // 保存节点编辑信息
  const handleSaveNode = useCallback(() => {
    editForm.validateFields().then(values => {
      if (editingNode) {
        // 更新节点数据
        const updatedNodes = nodes.map(node => {
          if (node.id === editingNode.id) {
            // 处理表达式
            const branchesToSave = values.branches?.map(item => ({
              ...item,
              fromNodeId: editingNode.id,
              expression: `#${item.left} ${item.operator} '${item.right}'`,
            }));

            return {
              ...node,
              data: {
                ...node.data,
                ...values,
                branches: branchesToSave,
              },
            };
          }
          return node;
        });
        setNodes(updatedNodes);
        if (editingNode.id && values.branches) {
          values.branches.forEach((branch: any) => {
            const targetNodeId = branch.toNodeId;

            // 检查连接是否已存在
            const connectionExists = edges.some(edge => edge.source === editingNode.id && edge.target === targetNodeId);

            // 如果连接不存在，则创建新连接
            if (!connectionExists) {
              const newEdge: Edge = {
                id: `e${editingNode.id}-${targetNodeId}`,
                source: editingNode.id,
                target: targetNodeId,
              };
              setEdges([...edges, newEdge]);
            }
          });
        }

        setDrawerVisible(false);
      }
    });
  }, [editingNode, nodes, editForm]);

  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.stopPropagation();
      setEditingNode(node);
      let formData = node.data || {};
      if (formData.branches) {
        formData.branches = formData.branches.map(item => {
          const { left, operator, right } = parseExpression(item.expression);
          return {
            ...item,
            left,
            operator,
            right,
          };
        });
      }
      editForm.setFieldsValue(formData);
      const config = nodeMetaData?.find(n => n.id == (node.type as unknown as number));
      if (config) {
        setNodeConfig(config);
        setDrawerVisible(true);
      }
    },
    [editForm, nodeMetaData]
  );

  // 生成带事件的节点
  const nodesWithEvents = nodes.map(n => ({
    ...n,
    data: {
      ...n.data,
      onHandleClick,
      handleDeleteNode,
      onClick: (event: React.MouseEvent) => handleNodeClick(event, n),
    }, // 添加点击事件},
  }));

  // 在组件内部添加计算新节点位置的函数
  const calculateNewNodePosition = useCallback((activeNodeId: string, nodes: AppNode[], edges: Edge[]) => {
    // 获取从激活节点出发的所有边
    const outgoingEdges = edges.filter(edge => edge.source === activeNodeId);

    if (outgoingEdges.length === 0) {
      // 如果没有出边，使用默认位置（在激活节点右侧）
      const activeNode = nodes.find(n => n.id === activeNodeId);
      return {
        x: activeNode ? activeNode.position.x + 300 : 500,
        y: activeNode ? activeNode.position.y : 500,
      };
    }

    // 获取已连接的节点中x坐标最大的节点作为参考
    let referenceNode: AppNode | null = null;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const edge of outgoingEdges) {
      const targetNode = nodes.find(n => n.id === edge.target);
      if (targetNode && targetNode.position.x > maxX) {
        maxX = targetNode.position.x;
      }
      if (targetNode && targetNode.position.y > maxY) {
        maxY = targetNode.position.y;
        referenceNode = targetNode;
      }
    }

    if (referenceNode) {
      // 使用参考节点的x坐标，y坐标向下偏移
      return {
        x: referenceNode.position.x,
        y: referenceNode.position.y + 100, // 向下偏移100像素
      };
    }

    // fallback到默认位置
    const activeNode = nodes.find(n => n.id === activeNodeId);
    return {
      x: activeNode ? activeNode.position.x + 300 : 500,
      y: activeNode ? activeNode.position.y : 500,
    };
  }, []);

  const nodeClassName = node => node.type;

  const onSave = () => {
    if (rfInstance) {
      const flow = rfInstance.toObject();
      const params = {
        id,
        nodes: nodes.map(node => ({
          nodeId: node.id,
          nodeTypeId: node.type,
          config: node.data,
          branches: node.data.branches,
        })),
        transitions: edges.map(edge => ({ fromNodeId: edge.source, toNodeId: edge.target })),

        definition: JSON.stringify(flow),
      };
      updateProcess(params).then(res => {
        message.success('保存成功');
        window.open(`/applicationList`, '_self');
      });
    }
  };

  // 加载状态显示
  if (loading) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        加载中...
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%' }} onClick={handleCanvasClick}>
      {/* <button onClick={undo}>
        ⬅️ 撤销
      </button> */}
      <Space className="absolute top-[86px] right-[58px] z-10">
        <Button type="primary" onClick={onSave}>
          保存
        </Button>
      </Space>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodesWithEvents}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onInit={setRfInstance}
          onConnect={onConnect}
          // isValidConnection={isValidConnection}
          onEdgesDelete={deleted => {
            deleted.forEach(edge => handleDeleteEdge(edge.id));
          }}
          // onConnect={onConnectNode}
          fitView={false}
          onViewportChange={onViewportChange}
          viewport={viewport}
          minZoom={0.25}
          maxZoom={2}
        >
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          <MiniMap zoomable pannable nodeClassName={nodeClassName} />
          <Controls />
        </ReactFlow>

        {/* 悬浮面板 */}
        {panelPos && (
          <div
            style={{
              position: 'absolute',
              top: panelPos.y,
              left: panelPos.x,
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: 6,
              padding: '8px 12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 10,
            }}
            onClick={e => e.stopPropagation()}
          >
            {(nodeMetaData ?? [])
              .filter(i => i.id != NODE_TYPE.START)
              .map((item: any) => (
                <div
                  key={item.id}
                  style={{ padding: '4px 0', cursor: 'pointer' }}
                  className={`flex items-center space-x-8 gap-2 ${atoms.hoverPrimary}`}
                  onClick={() => {
                    if (!activeNode) return;

                    // 计算新节点位置
                    const newPosition = calculateNewNodePosition(activeNode, nodes, edges);
                    const newNode: AppNode = {
                      id: `${NODE_TYPE_MAP[item.id as NODE_TYPE]}${nodes.length + 1}`,
                      type: item.id,
                      position: newPosition,
                      data: { label: item.name + nodes.length },
                    };
                    const newEdge: Edge = {
                      id: `e${activeNode}-${newNode.id}`,
                      source: activeNode!,
                      target: newNode.id,
                    };
                    setNodes([...nodes, newNode]);
                    setEdges([...edges, newEdge]);
                    setPanelPos(null);
                    setActiveNode(null);
                  }}
                >
                  {NODE_TYPE_ICON[item.id as NODE_TYPE]}
                  {item.name}
                </div>
              ))}
          </div>
        )}

        {/* 新增节点编辑抽屉 */}
        {drawerVisible && (
          <Drawer
            title={<>{nodeConfig?.name}</>}
            width={600}
            closable={false}
            onClose={() => {
              setDrawerVisible(false);
              editForm.resetFields();
            }}
            open={true}
          >
            {!isEmpty(nodeConfig?.fields) ||
            !isEmpty(nodeConfig?.variables) ||
            nodeConfig.id === NODE_TYPE.CONDITION ? (
              <Form form={editForm} layout="vertical">
                <NodeForm
                  key={nodeConfig.id}
                  node={nodeConfig}
                  form={editForm}
                  upstreamNodes={editingNode ? getUpstreamNodeVariables(editingNode.id, nodes, edges) : []}
                  availableTargetNodes={editingNode ? getAvailableTargetNodes(editingNode.id, nodes, edges) : []}
                />
                <Form.Item>
                  <Space>
                    <Button type="primary" onClick={handleSaveNode}>
                      保存
                    </Button>
                    <Button onClick={() => setDrawerVisible(false)}>取消</Button>
                  </Space>
                </Form.Item>
              </Form>
            ) : (
              <Empty description="暂无配置信息" />
            )}
          </Drawer>
        )}
      </ReactFlowProvider>
    </div>
  );
};

export default Workflow;
